// Scroll-based animations system
class ScrollAnimations {
  constructor() {
    this.elements = new Map()
    this.scrollPosition = 0
    this.ticking = false
    
    this.init()
  }

  init() {
    this.setupObserver()
    this.bindEvents()
    this.setupScrollProgress()
  }

  // Intersection Observer for scroll animations
  setupObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const element = entry.target
        
        if (entry.isIntersecting) {
          element.classList.add('animate-in')
          element.classList.remove('animate-out')
        } else {
          element.classList.remove('animate-in')
          element.classList.add('animate-out')
        }
      })
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    })
  }

  // Add element to animation system
  observe(element, animationType = 'fadeInUp') {
    element.classList.add('animate-element', animationType)
    this.observer.observe(element)
    this.elements.set(element, animationType)
  }

  // Remove element from animation system
  unobserve(element) {
    this.observer.unobserve(element)
    this.elements.delete(element)
  }

  // Bind scroll events
  bindEvents() {
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.handleScroll()
          this.ticking = false
        })
        this.ticking = true
      }
    }, { passive: true })
  }

  // Handle scroll events
  handleScroll() {
    this.scrollPosition = window.pageYOffset
    this.updateScrollProgress()
    this.updateParallaxElements()
  }

  // Setup scroll progress bar
  setupScrollProgress() {
    const progressBar = document.createElement('div')
    progressBar.className = 'scroll-progress'
    progressBar.innerHTML = '<div class="scroll-progress-bar"></div>'
    document.body.appendChild(progressBar)
  }

  // Update scroll progress bar
  updateScrollProgress() {
    const scrollPercent = (this.scrollPosition / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    const progressBar = document.querySelector('.scroll-progress-bar')
    if (progressBar) {
      progressBar.style.width = `${Math.min(scrollPercent, 100)}%`
    }
  }

  // Update parallax elements
  updateParallaxElements() {
    const parallaxElements = document.querySelectorAll('.parallax')
    parallaxElements.forEach(element => {
      const rect = element.getBoundingClientRect()
      const speed = element.dataset.speed || 0.5
      const yPos = -(this.scrollPosition * speed)
      element.style.transform = `translateY(${yPos}px)`
    })
  }

  // Stagger animations
  staggerAnimations(selector, delay = 100) {
    const elements = document.querySelectorAll(selector)
    elements.forEach((element, index) => {
      element.style.animationDelay = `${index * delay}ms`
      this.observe(element)
    })
  }

  // Cleanup
  destroy() {
    this.observer.disconnect()
    this.elements.clear()
    window.removeEventListener('scroll', this.handleScroll)
  }
}

// Animation helper functions
export const animateOnScroll = (element, animation = 'fadeInUp') => {
  if (window.scrollAnimations) {
    window.scrollAnimations.observe(element, animation)
  }
}

export const staggerElements = (selector, delay = 100) => {
  if (window.scrollAnimations) {
    window.scrollAnimations.staggerAnimations(selector, delay)
  }
}

// Initialize scroll animations
export const initScrollAnimations = () => {
  if (!window.scrollAnimations) {
    window.scrollAnimations = new ScrollAnimations()
  }
  return window.scrollAnimations
}

export default ScrollAnimations